import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { Link } from "./Link";
import { NavatticDemoButton } from "./NavatticDemoButton";

export const button = tv({
  base: cn(
    "uipm:inline-flex uipm:items-center uipm:justify-center uipm:text-center uipm:transition-all uipm:duration-200 uipm:ease-out uipm:focus-visible:outline-none uipm:focus-visible:ring-2 uipm:focus-visible:ring-ring uipm:focus-visible:ring-offset-2 uipm:disabled:pointer-events-none uipm:disabled:opacity-50 uipm:cursor-pointer",
    "uipm:rounded-[var(--button-border-radius)] uipm:min-w-[var(--button-min-width)]",
    "uipm:text-[var(--button-font-size)] uipm:leading-[var(--button-line-height)]",
    "uipm:font-[var(--font-family-sans)] uipm:font-semibold",
    "uipm:border-2 uipm:border-solid",
    "uipm:no-underline uipm:whitespace-nowrap",
  ),
  variants: {
    variant: {
      primary: cn(
        "uipm:bg-[var(--surface-surface-accent)] uipm:text-[var(--typography-colors-text-inverted)] uipm:border-transparent",
        "uipm:hover:bg-[var(--color-color-accent-hover,#2D5C3F)] uipm:active:bg-[var(--color-color-accent-focus,#1E3D2A)]",
        "uipm:disabled:bg-[var(--surface-surface-accent-disabled)]",
      ),
      secondary: cn(
        "uipm:bg-[var(--surface-surface-accent-secondary)] uipm:text-[var(--typography-colors-label-inverted)] uipm:border-transparent",
        "uipm:hover:bg-[var(--color-color-accent-secondary-hover)] uipm:active:bg-[var(--color-color-accent-secondary-focus)] uipm:active:text-[var(--typography-colors-text-inverted)]",
        "uipm:disabled:bg-[var(--surface-surface-accent-disabled)] uipm:disabled:text-[var(--typography-colors-text-inverted)]",
      ),
      tertiary: cn(
        "uipm:bg-transparent uipm:text-[var(--surface-surface-accent)] uipm:border-[var(--surface-surface-accent)]",
        "uipm:hover:border-[var(--color-color-accent-hover)] uipm:hover:text-[var(--color-color-accent-hover)]",
        "uipm:active:border-[var(--color-color-accent-focus)] uipm:active:text-[var(--color-color-accent-focus)]",
        "uipm:disabled:border-[var(--surface-surface-accent-disabled)] uipm:disabled:text-[var(--typography-colors-text-disabled)]",
      ),
      "inverted-fill": cn(
        "uipm:bg-[var(--surface-surface-primary,#FFFFFF)] uipm:text-[var(--typography-colors-label-inverted)] uipm:border-transparent",
        "uipm:hover:bg-[var(--surface-surface-secondary,#F4F4F4)] uipm:hover:text-[var(--typography-colors-text-disabled,#D3E9DB)]",
        "uipm:active:bg-[var(--surface-surface-primary,#FFFFFF)]",
        "uipm:disabled:bg-[var(--surface-surface-primary)] uipm:disabled:text-[var(--typography-colors-text-disabled)]",
      ),
      "inverted-border": cn(
        "uipm:bg-transparent uipm:text-[var(--typography-colors-text-inverted,#FFF)] uipm:border-[var(--surface-surface-primary)]",
        "uipm:hover:bg-transparent uipm:hover:text-[var(--typography-colors-text-inverted,#FFF)]",
        "uipm:active:bg-transparent",
        "uipm:disabled:opacity-50",
      ),
    },
    size: {
      small: cn("uipm:px-4 uipm:h-[50px]"),
      medium: cn("uipm:px-4 uipm:h-[58px]"),
      large: cn(
        "uipm:px-4 uipm:h-[64px] uipm:!text-[18px] uipm:!leading-[26px]",
      ),
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "small",
  },
});

export type ButtonProps = VariantProps<typeof button> & {
  className?: string;
  children?: React.ReactNode;
  href?: string;
  target?: string;
  noFollow?: boolean;
  onClick?: React.MouseEventHandler;
  type?: "submit" | "button";
  disabled?: boolean;
  text?: string;
  analyticsEvent?: "" | "download_app_click" | "ctaClick";
  id?: string;
  /**
   * Storyblok `button.type === "navattic"`: opens this Navattic demo in a
   * lightbox instead of navigating. Plain strings so the button resolver can
   * run inside Server Components; the click lives in `NavatticDemoButton`.
   */
  navatticUrl?: string;
  navatticTitle?: string;
  /** Separate mobile flow of the same demo (same title); the desktop one opens without it. */
  navatticMobileUrl?: string;
};

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size = "small",
      href,
      target,
      noFollow,
      children,
      onClick,
      type = "button",
      disabled,
      analyticsEvent = "ctaClick",
      navatticUrl,
      navatticTitle,
      navatticMobileUrl,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = cn(button({ variant, size }), className);

    if (navatticUrl) {
      return (
        <NavatticDemoButton
          ref={ref as React.Ref<HTMLButtonElement>}
          url={navatticUrl}
          title={navatticTitle}
          mobileUrl={navatticMobileUrl}
          disabled={disabled}
          onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
          className={buttonClassName}
          {...props}
        >
          {children}
        </NavatticDemoButton>
      );
    }

    if (href) {
      return (
        <Link
          href={href}
          target={target}
          className={buttonClassName}
          ref={ref as React.Ref<HTMLAnchorElement>}
          onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
          style={{ display: "inline-flex", textDecoration: "none" }} // Ensure flex display is maintained
          noFollow={noFollow}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        disabled={disabled}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        className={buttonClassName}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
